import { apiGetCall, apiPostCall } from 'src/services/api/api.core';
import { EApiEndpoint } from 'src/config/enums';
import { applyValues, getCookie } from 'src/utils';
import type { TAppointmentDate } from 'src/services/api/api.types';
import { capturePostHogEvent, capturePostHogException } from 'src/utils/posthog';

type TAppointmentBookingRequest = {
    patientId: string;
    clinicId: number;
    date: string;
    timeOfDay: string;
    contactMethod: string;
    practitionerId: string | null;
    fbcEventId: string | null;
    interestedInHair: boolean;
    interestedInSkin: boolean;
};

export const postAppointmentBookingRequest = async (data: TAppointmentBookingRequest) => {
    const captureContext = {
        extra: {
            utmtracking: getCookie('utmtracking'),
            fbp: getCookie('_fbp'),
            fbc: getCookie('_fbc')
        }
    };

    const pageInterest = getCookie('pageinterest');
    const pageUrl = typeof window !== 'undefined' ? window.location.href : undefined;
    const headers: Record<string, string> = {
        ...(captureContext.extra.fbp && { 'X-Fbp': captureContext.extra.fbp }),
        ...(captureContext.extra.fbc && { 'X-Fbc': captureContext.extra.fbc }),
        ...(captureContext.extra.utmtracking && { 'X-Ad-Landing-Query': captureContext.extra.utmtracking }),
        ...(data.fbcEventId && { 'X-Fb-Event-Id': data.fbcEventId }),
        ...(pageUrl && { 'X-Page-Url': pageUrl }),
        ...(pageInterest && { 'X-Page-Interest': pageInterest })
    };

    let result;
    try {
        result = await apiPostCall(
            EApiEndpoint.AppointmentBookingRequest,
            JSON.stringify(data),
            { credentials: true, headers }
        );
    } catch (err) {
        capturePostHogException(err as Error, {
            utmtracking: captureContext.extra.utmtracking,
            fbp: captureContext.extra.fbp,
            fbc: captureContext.extra.fbc
        });
        throw err;
    }

    // Fire conversion event after successful API response
    // Track conversion in PostHog
    capturePostHogEvent('appointment_booking_conversion', {
        clinic_id: data.clinicId,
        practitioner_id: data.practitionerId,
        contact_method: data.contactMethod,
        appointment_date: data.date,
        time_of_day: data.timeOfDay,
        utm_tracking: captureContext.extra.utmtracking,
        fbp: captureContext.extra.fbp,
        fbc: captureContext.extra.fbc,
        fb_event_id: data.fbcEventId
    });

    return result;
};

type TAppointmentBookingRequestTimes = {
    clinicId: number;
    appointmentTypeId: string;
};

export const getAppointmentBookingRequestTimes = async (
    params: TAppointmentBookingRequestTimes
): Promise<TAppointmentDate[] | null> => {
    return await apiGetCall(
        applyValues(EApiEndpoint.AppointmentBookingRequestAppointmentTime, params)
    );
};
